/*
 * Copyright (c) AVPS Technologies Inc 2025
 *
 * One subscribed course in the student's purchase section: the plan it was
 * bought on, when it started, where it stands, and the bills raised against it.
 */

import { motion } from 'framer-motion';
import { FaFileInvoiceDollar, FaSyncAlt } from 'react-icons/fa';
import {
    getPaymentStatusTone,
    getSubscriptionStatusLabel,
    getSubscriptionStatusTone,
} from '../../../../../../utils/subscriptionDisplay';

const DetailField = ({ label, children }) => (
    <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
        <dd className="mt-1 text-sm font-medium text-slate-900">{children}</dd>
    </div>
);

const SubscriptionCard = ({
    course,
    index = 0,
    formatCurrency,
    formatDate,
    getDomainTextColor,
    getDomainBackgroundColor,
    getDomainBorderColor,
}) => {
    const statusLabel = getSubscriptionStatusLabel(course?.status);
    const bills = Array.isArray(course?.bills) ? course.bills : [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.05, 0.3) }}
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
        >
            <div
                className="px-5 py-4 border-b"
                style={{ backgroundColor: getDomainBackgroundColor?.(), borderColor: getDomainBorderColor?.() }}
            >
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/70"
                            style={{ color: getDomainTextColor?.() }}
                        >
                            <FaSyncAlt className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-slate-900 break-words">
                                {course?.courseName}
                            </h3>
                            <p className="text-xs text-slate-600 mt-0.5">
                                Subscription course
                                {course?.isTrial ? ' · Trial' : ''}
                            </p>
                        </div>
                    </div>

                    {statusLabel && (
                        <span
                            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium border ${getSubscriptionStatusTone(course?.status)}`}
                        >
                            {statusLabel}
                        </span>
                    )}
                </div>
            </div>

            <div className="px-5 py-4">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <DetailField label="Subscription Plan">
                        {course?.planName}
                        {course?.planAmount !== null && course?.planAmount !== undefined && (
                            <span className="block text-xs font-normal text-slate-600 mt-0.5">
                                {formatCurrency ? formatCurrency(course.planAmount) : course.planAmount}
                                {course?.billingCycle ? ` / ${course.billingCycle}` : ''}
                            </span>
                        )}
                    </DetailField>

                    <DetailField label="Billing Cycle">
                        {course?.billingCycle || 'N/A'}
                    </DetailField>

                    <DetailField label="Subscription Created">
                        {formatDate ? formatDate(course?.createdDate) : course?.createdDate || 'N/A'}
                    </DetailField>

                    <DetailField label="Next Billing Date">
                        {formatDate ? formatDate(course?.nextBillingDate) : course?.nextBillingDate || 'N/A'}
                    </DetailField>
                </dl>

                <div className="mt-5 border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                        <FaFileInvoiceDollar className="w-3.5 h-3.5 text-slate-400" />
                        <h4 className="text-sm font-semibold text-slate-900">Billing &amp; Payment</h4>
                        <span className="text-xs text-slate-500">
                            {course?.autoPaymentEnabled ? '· Auto payment on' : '· Auto payment off'}
                        </span>
                    </div>

                    {bills.length === 0 ? (
                        <p className="text-sm text-slate-600">
                            No bills have been raised for this subscription yet.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                                        <th scope="col" className="py-2 pr-4 font-medium">Invoice</th>
                                        <th scope="col" className="py-2 pr-4 font-medium">Billing Period</th>
                                        <th scope="col" className="py-2 pr-4 font-medium">Due Date</th>
                                        <th scope="col" className="py-2 pr-4 font-medium">Amount</th>
                                        <th scope="col" className="py-2 font-medium">Payment Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {bills.map((bill) => (
                                        <tr key={bill.key} className="text-slate-700">
                                            <td className="py-2.5 pr-4 whitespace-nowrap">
                                                {bill.invoiceNumber || 'N/A'}
                                                {bill.trialBilling && (
                                                    <span className="ml-2 text-xs text-slate-500">(Trial)</span>
                                                )}
                                            </td>
                                            <td className="py-2.5 pr-4 whitespace-nowrap">
                                                {bill.periodStart || bill.periodEnd
                                                    ? `${formatDate ? formatDate(bill.periodStart) : bill.periodStart} – ${formatDate ? formatDate(bill.periodEnd) : bill.periodEnd}`
                                                    : 'N/A'}
                                            </td>
                                            <td className="py-2.5 pr-4 whitespace-nowrap">
                                                {formatDate ? formatDate(bill.dueDate) : bill.dueDate || 'N/A'}
                                            </td>
                                            <td className="py-2.5 pr-4 whitespace-nowrap">
                                                {bill.amount === null || bill.amount === undefined
                                                    ? 'N/A'
                                                    : (formatCurrency ? formatCurrency(bill.amount) : bill.amount)}
                                            </td>
                                            <td className="py-2.5 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 rounded-md text-xs font-medium border ${getPaymentStatusTone(bill.paymentStatus)}`}
                                                >
                                                    {bill.paymentStatus}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default SubscriptionCard;
